import { Controller, useForm } from "react-hook-form";

import { Button, Select, toastError } from "@alpacahq/ui/old";

import AmplitudeProvider from "~/AmplitudeProvider";
import {
  type InvestmentObjective,
  patchAccount,
  patchFinancialProfile,
} from "~/v2/api/rest/account";
import { Field } from "~/v2/components/form";
import {
  INVESTMENT_OBJECTIVE_LABEL,
  INVESTMENT_OBJECTIVE_OPTIONS,
} from "~/v2/constants/investmentObjective";
import { getRangesAsOptionsArray } from "~/v2/utils/ranges";
import { useLock, useMutationRequest } from "../../../../api/hooks";
import { RadiosInput } from "../../optionsOptIn/RadiosInput";
import { Back } from "../back";
import { FUNDING_SOURCE } from "../constants";
import type { StepProps } from "../session";

type Fields = {
  annual_income: string;
  total_net_worth: string;
  liquid_net_worth: string;
  funding_source: string;
  investment_objective: string;
};

const FinancialProfile = ({ session }: StepProps) => {
  const lock = useLock();

  // we need session below, so we spread these here
  const { account, details, next } = session;

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isValid },
  } = useForm<Fields>({
    mode: "all",
    defaultValues: {
      funding_source: details.funding_source?.[0] ?? "",
      annual_income:
        JSON.stringify({
          annual_income_min: details.annual_income_min || 0,
          annual_income_max: details.annual_income_max || 24999,
        }) || "",
      liquid_net_worth:
        JSON.stringify({
          liquid_net_worth_min: details.liquid_net_worth_min || 0,
          liquid_net_worth_max: details.liquid_net_worth_max || 24999,
        }) || "",
      total_net_worth:
        JSON.stringify({
          total_net_worth_min: details.total_net_worth_min || 0,
          total_net_worth_max: details.total_net_worth_max || 24999,
        }) || "",
      investment_objective: account.investment_objective ?? "",
    },
  });

  // investment_objective lives on the Account, not OwnerDetails, so it needs
  // a separate patchAccount call alongside the financial_profile_step patch.
  const financialMutation = useMutationRequest(async (values: Fields) => {
    await patchAccount(account.id, {
      investment_objective: values.investment_objective as InvestmentObjective,
    });

    return patchFinancialProfile(account.id, {
      // has to be an array but we don't use multi-select
      funding_source: [values.funding_source].flat(),
      // parse ranges, they are stored as strings in the form
      ...JSON.parse(values.annual_income),
      ...JSON.parse(values.total_net_worth),
      ...JSON.parse(values.liquid_net_worth),
    });
  });

  const sendPatch = (values: Fields) => {
    if (lock.locked) {
      return;
    }

    lock.lock();

    financialMutation.mutate(values, {
      onSuccess: () => {
        // call amplitude event
        AmplitudeProvider.dispatch(
          "onboardingv2_financial_profile_step_complete",
        );

        next();
      },
      onError: () => {
        // show an error toast
        toastError("Error updating financial profile");
      },
      onSettled: () => lock.unlock(),
    });
  };

  return (
    <div className="mb-16">
      <h2 className="mb-8 font-bold text-lg">{session.step.title}</h2>
      <form onSubmit={handleSubmit(sendPatch)}>
        <div className="mt-4 flex justify-between">
          <div className="mr-4 w-[50%]">
            <Field
              label="Annual Income"
              required={true}
              error={!!errors.annual_income}
              errorMsg="Required"
            >
              <Controller
                name="annual_income"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    placeholder="None selected"
                    options={getRangesAsOptionsArray(field.name)}
                    {...field}
                  />
                )}
              />
            </Field>
          </div>
          <div className="w-[50%]">
            <Field
              label="Estimated Net Worth"
              required={true}
              error={!!errors.total_net_worth}
              errorMsg="Required"
            >
              <Controller
                name="total_net_worth"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    placeholder="None selected"
                    options={getRangesAsOptionsArray(field.name)}
                    {...field}
                  />
                )}
              />
            </Field>
          </div>
        </div>
        <div className="mt-4 flex justify-between">
          <div className="mr-4 w-[50%]">
            <Field
              label="Investible / Liquid Assets"
              required={true}
              error={!!errors.liquid_net_worth}
              errorMsg="Required"
            >
              <Controller
                name="liquid_net_worth"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    placeholder="None selected"
                    options={getRangesAsOptionsArray(field.name)}
                    {...field}
                  />
                )}
              />
            </Field>
          </div>
          <div className="w-[50%]">
            <Field
              label="Account Funding Source"
              required={true}
              error={!!errors.funding_source}
              errorMsg="Required"
            >
              <Controller
                name="funding_source"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    placeholder="None selected"
                    options={FUNDING_SOURCE}
                    {...field}
                  />
                )}
              />
            </Field>
          </div>
        </div>
        <div className="mt-6">
          <Controller
            name="investment_objective"
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState: { error } }) => (
              <RadiosInput
                id="investment_objective"
                label={INVESTMENT_OBJECTIVE_LABEL}
                value={field.value}
                options={INVESTMENT_OBJECTIVE_OPTIONS}
                onSelect={field.onChange}
                errorMessage={error ? "Required" : undefined}
              />
            )}
          />
        </div>
        <div className="mt-8 flex justify-end">
          <Back patch={true} session={session} />
          <Button
            className="ml-1"
            type="submit"
            disabled={isSubmitting || !isValid}
          >
            Continue
          </Button>
        </div>
      </form>
    </div>
  );
};

export default FinancialProfile;
